const MAX_BODY_LENGTH = 500;

class APIError extends Error {
  name = 'APIError';

  status: number;

  statusText?: string;

  url?: string;

  body?: string;

  constructor(response: Response, body?: string) {
    const truncatedBody = body?.slice(0, MAX_BODY_LENGTH);
    const bodyPreview = truncatedBody ? ` - ${truncatedBody}` : '';
    super(`${response.status} (${response.statusText})${bodyPreview}`);
    this.status = response.status;
    this.statusText = response.statusText;
    this.url = response.url;
    this.body = body;
    this.cause = body;

    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Creates an APIError with the response body included.
 *
 * @param response The Response object from the failed fetch request.
 * @returns A promise that resolves to an APIError instance with the response body.
 */
export async function createAPIError(response: Response): Promise<APIError> {
  const body = await response.text().catch(() => undefined);
  return new APIError(response, body);
}

/**
 * Type guard to check if an error is an instance of APIError.
 *
 * @param error The error to check.
 * @returns `true` if the error is an `APIError`, `false` otherwise.
 */
export function isApiError(error: unknown): error is APIError {
  return error instanceof APIError;
}
